import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Grid } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

// 40 distinct colors for chart segments
const COLORS = [
    '#7cb342', // green
    '#5e35b1', // purple
    '#ef5350', // red
    '#2e7d32', // dark green
    '#1e3a8a', // navy
    '#d4a017', // gold
    '#26a69a', // teal
    '#ec407a', // pink
    '#ff7043', // deep orange
    '#ab47bc', // purple
    '#66bb6a', // light green
    '#42a5f5', // blue
    '#ffa726', // orange
    '#8d6e63', // brown
    '#78909c', // blue grey
    '#9ccc65', // lime
    '#7e57c2', // deep purple
    '#ef5350', // red
    '#26c6da', // cyan
    '#ffca28', // amber
    '#29b6f6', // light blue
    '#ab47bc', // purple
    '#66bb6a', // green
    '#ffa726', // orange
    '#ec407a', // pink
    '#5c6bc0', // indigo
    '#26a69a', // teal
    '#ff7043', // deep orange
    '#9ccc65', // lime
    '#8d6e63', // brown
    '#42a5f5', // blue
    '#ffca28', // amber
    '#7e57c2', // deep purple
    '#66bb6a', // green
    '#ef5350', // red
    '#29b6f6', // light blue
    '#ffa726', // orange
    '#ec407a', // pink
    '#26c6da', // cyan
    '#78909c', // blue grey
];

const LegendItem = ({ color, label, value }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box
            sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0
            }}
        />
        <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {value !== undefined ? `${value}%` : ''}
        </Typography>
    </Stack>
);

const CaseDistributionChart = ({ title = "Case Distribution by Subcategory" }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                // Get params with organization_id if applicable
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];
                const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head', 'director cid', 'director ncpa', 'deputy director','oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
                const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

                const params = {
                    //  date_from: '2024-01-01',
                    // date_to: '2024-12-31'
                };
                if (hasTargetRole) {
                    const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
                    if (orgId) params.organization_id = orgId;
                }

                const targetEmployee_id_Roles = ['case officer', 'first respondent', 'counselling officer', 'legal officer'];
                const needToPassUserId = normalizedRoles.some(r => targetEmployee_id_Roles.includes(r));
                if (needToPassUserId) {
                    const employeeId = storedUser?.employee?.id ?? storedUser?.employee_id ?? null;
                    if (employeeId) params.assigned_to_officer_id = employeeId;
                }

                const res = await DashboardServices.getDashboardCaseDistributionBySubcategory(params);
                const payload = res?.data ?? res;
                // Expected: { view: { msg, data: [{subcategory_id, subcategory_name, category_name, case_count, percentage}] } }
                const dataArray = payload?.view?.data ?? payload?.data ?? payload ?? [];

                // Transform data for chart (limit to top items to avoid overcrowding)
                const topItems = Array.isArray(dataArray)
                    ? dataArray
                        .sort((a, b) => (b.percentage ?? 0) - (a.percentage ?? 0))
                        .slice(0, 10) // Show top 10 subcategories
                    : [];

                const formatted = topItems.map((item) => ({
                    name: item.subcategory_name ?? item.name ?? 'Unknown',
                    value: Number(item.case_count) || 0,
                    percentage: parseFloat(item.percentage) || 0,
                    count: Number(item.case_count) || 0,
                    category: item.category_name ?? '',
                }));

                console.log('Case Distribution Data:', formatted);
                if (mounted) setChartData(formatted);
            } catch (err) {
                console.error('Failed to load case distribution data', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, []);

    //if (loading) return <WidgetCard><CenteredSpinner /></WidgetCard>;

    return (
        <WidgetCard>
            <MainCardTitle>{title}</MainCardTitle>

            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            ) : (
                <Grid container spacing={3}>
                    {/* Chart */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                            <PieChart width={300} height={300}>
                                <Pie
                                    data={chartData}
                                    cx={150}
                                    cy={150}
                                    innerRadius={80}
                                    outerRadius={130}
                                    fill="#8884d8"
                                    paddingAngle={2}
                                    dataKey="value"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </Box>
                    </Grid>

                    {/* Legend */}
                    <Grid item size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                            <Grid container spacing={2} >

                                {chartData.map((item, index) => (
                                    <Grid className={index % 2 === 0 ? "" : "pl-7"} item size={{ xs: 12, md: 6 }} key={index}>
                                        <LegendItem
                                            color={COLORS[index % COLORS.length]}
                                            label={item.name}
                                            value={item.percentage}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            )}
        </WidgetCard>
    );
};

export default CaseDistributionChart;
